import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";
import { resolveImage, API_BASE } from "@/lib/api";

/**
 * Extract a YouTube video ID from any common YouTube URL form:
 *   https://www.youtube.com/watch?v=XXXX
 *   https://youtu.be/XXXX
 *   https://youtube.com/shorts/XXXX
 *   https://www.youtube.com/embed/XXXX
 */
function youtubeIdFrom(url) {
  if (!url) return null;
  const s = String(url).trim();
  // youtu.be/<id>
  let m = s.match(/youtu\.be\/([^?&/]+)/i);
  if (m) return m[1];
  // youtube.com/shorts/<id>
  m = s.match(/youtube\.com\/shorts\/([^?&/]+)/i);
  if (m) return m[1];
  // youtube.com/embed/<id>
  m = s.match(/youtube\.com\/embed\/([^?&/]+)/i);
  if (m) return m[1];
  // youtube.com/watch?v=<id>
  m = s.match(/[?&]v=([^?&]+)/i);
  if (m) return m[1];
  return null;
}

/**
 * Hero welcome video. Supports:
 *   1. YouTube URLs (Shorts, regular, youtu.be) — embeds an iframe on play
 *   2. Self-hosted uploads via `/api/videos/<id>` (with 720p variant + Range)
 *   3. Any direct video URL
 *
 * Always shows a poster + big gold play button. NOT autoplay.
 */
export default function HeroVideo({ src, poster, fallbackImg, alt }) {
  const ref = useRef(null);
  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [resolvedSrc, setResolvedSrc] = useState("");

  const ytId = youtubeIdFrom(src);
  const posterSrc = poster
    ? resolveImage(poster)
    : (ytId
        ? `https://i.ytimg.com/vi/${ytId}/maxresdefault.jpg`
        : (fallbackImg ? resolveImage(fallbackImg) : ""));

  // For self-hosted: pick best variant (mobile gets 720p when available)
  useEffect(() => {
    if (ytId) return; // YouTube path doesn't need this
    let cancelled = false;
    async function pick() {
      if (!src) return;
      const base = resolveImage(src);
      const m = String(src).match(/\/api\/videos\/([^?]+)/);
      if (!m) { setResolvedSrc(base); return; }
      const id = m[1];
      try {
        const r = await fetch(`${API_BASE}/api/videos/${id}/meta`);
        const meta = r.ok ? await r.json() : {};
        const has720 = !!(meta?.variants?.["720p"]);
        const isMobile = window.matchMedia("(max-width: 1024px)").matches;
        const saveData = navigator.connection?.saveData === true;
        if (!cancelled) setResolvedSrc((isMobile || saveData) && has720 ? `${API_BASE}/api/videos/${id}?variant=720p` : base);
      } catch { if (!cancelled) setResolvedSrc(base); }
    }
    pick();
    return () => { cancelled = true; };
  }, [src, ytId]);

  const start = () => {
    setStarted(true);
    if (ytId) { setPlaying(true); return; }
    setTimeout(() => {
      const v = ref.current; if (!v) return;
      v.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }, 0);
  };

  const togglePause = () => {
    if (ytId) return; // iframe controls its own playback
    const v = ref.current; if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  return (
    <div className="relative block aspect-[4/5] max-w-[520px] mx-auto overflow-hidden border border-white/5 bg-[#0a0a0a]" data-testid="hero-video">
      {ytId ? (
        started ? (
          <iframe
            title="Welcome video"
            data-testid="hero-video-iframe"
            src={`https://www.youtube.com/embed/${ytId}?autoplay=1&playsinline=1&modestbranding=1&rel=0&iv_load_policy=3`}
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
          />
        ) : (
          <img
            src={posterSrc}
            alt={alt || "Welcome video"}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.src = `https://i.ytimg.com/vi/${ytId}/hqdefault.jpg`; }}
          />
        )
      ) : (
        <video
          ref={ref}
          src={resolvedSrc || resolveImage(src)}
          poster={posterSrc || undefined}
          className="w-full h-full object-cover"
          controls={started}
          preload="metadata"
          playsInline
          onPause={() => setPlaying(false)}
          onPlay={() => setPlaying(true)}
          onEnded={() => setPlaying(false)}
        />
      )}

      {!started && (
        <>
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/70 via-transparent to-black/20" />
          <button
            type="button"
            onClick={start}
            aria-label="Play welcome video"
            data-testid="hero-video-play"
            className="absolute inset-0 flex items-center justify-center group"
          >
            <span className="relative inline-flex items-center justify-center w-24 h-24 rounded-full bg-[#C9A055]/90 group-hover:bg-[#C9A055] group-hover:scale-110 transition-all shadow-[0_0_40px_rgba(201,160,85,0.4)]">
              <span className="absolute inset-0 rounded-full animate-ping bg-[#C9A055]/30" />
              <Play size={36} className="text-black ml-1.5" fill="currentColor"/>
            </span>
          </button>
        </>
      )}
      {started && !ytId && (
        <button
          type="button"
          onClick={togglePause}
          aria-label={playing ? "Pause" : "Play"}
          className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/60 hover:bg-[#C9A055]/80 text-[#F4F1EA] hover:text-black inline-flex items-center justify-center transition-colors backdrop-blur-sm"
          data-testid="hero-video-toggle"
        >
          {playing ? <Pause size={16}/> : <Play size={16} fill="currentColor" className="ml-0.5"/>}
        </button>
      )}
      <span className="sr-only">{alt || "Welcome video"}</span>
    </div>
  );
}
